import { useState, useCallback, useMemo } from "react";
import type { SubStepDefinition } from "../onboarding.types";

export type UseSubStepsArgs = {
  /** Sub-steps from the onboarding step definition */
  subSteps: SubStepDefinition[] | undefined;
  /** Callback when all sub-steps are completed */
  onComplete: () => void;
};

export type UseSubStepsResult = {
  /** Whether this step has sub-steps */
  hasSubSteps: boolean;
  /** Total number of sub-steps */
  totalSubSteps: number;
  /** Current sub-step index (0-based) */
  currentSubStepIndex: number;
  /** Current sub-step definition */
  currentSubStep: SubStepDefinition | null;
  /** Whether this is the first sub-step */
  isFirstSubStep: boolean;
  /** Whether this is the last sub-step */
  isLastSubStep: boolean;
  /** Navigate to the next sub-step (or call onComplete if last) */
  nextSubStep: () => void;
  /** Navigate to the previous sub-step */
  previousSubStep: () => void;
  /** Navigate to a specific sub-step by index */
  goToSubStep: (index: number) => void;
  /** Reset to the first sub-step */
  resetSubSteps: () => void;
};

/**
 * Hook to manage sub-step navigation within a parent onboarding step.
 *
 * @example
 * ```tsx
 * const {
 *   hasSubSteps,
 *   currentSubStep,
 *   isFirstSubStep,
 *   isLastSubStep,
 *   nextSubStep,
 *   previousSubStep,
 * } = useSubSteps({
 *   subSteps: step.sub_steps,
 *   onComplete: handleStepComplete,
 * });
 *
 * if (!hasSubSteps) {
 *   // Render single-screen content
 *   return <ProfileFormContainer onComplete={onComplete} />;
 * }
 *
 * // Render current sub-step
 * switch (currentSubStep?.slug) {
 *   case "timezone":
 *     return <TimezoneStep onNext={nextSubStep} />;
 *   case "website":
 *     return <WebsiteStep onNext={nextSubStep} onBack={previousSubStep} />;
 *   // ...
 * }
 * ```
 */
export function useSubSteps({
  subSteps,
  onComplete,
}: UseSubStepsArgs): UseSubStepsResult {
  const [currentSubStepIndex, setCurrentSubStepIndex] = useState(0);

  // Sort sub-steps by sequence
  const sortedSubSteps = useMemo(() => {
    if (!subSteps || subSteps.length === 0) return [];
    return [...subSteps].sort((a, b) => a.sequence - b.sequence);
  }, [subSteps]);

  const hasSubSteps = sortedSubSteps.length > 0;
  const totalSubSteps = sortedSubSteps.length;
  const currentSubStep = sortedSubSteps[currentSubStepIndex] ?? null;
  const isFirstSubStep = currentSubStepIndex === 0;
  const isLastSubStep = currentSubStepIndex >= totalSubSteps - 1;

  const nextSubStep = useCallback(() => {
    if (isLastSubStep) {
      // All sub-steps completed, call parent onComplete
      onComplete();
    } else {
      setCurrentSubStepIndex((prev) => prev + 1);
    }
  }, [isLastSubStep, onComplete]);

  const previousSubStep = useCallback(() => {
    if (!isFirstSubStep) {
      setCurrentSubStepIndex((prev) => prev - 1);
    }
  }, [isFirstSubStep]);

  const goToSubStep = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalSubSteps) {
        setCurrentSubStepIndex(index);
      }
    },
    [totalSubSteps]
  );

  const resetSubSteps = useCallback(() => {
    setCurrentSubStepIndex(0);
  }, []);

  return {
    hasSubSteps,
    totalSubSteps,
    currentSubStepIndex,
    currentSubStep,
    isFirstSubStep,
    isLastSubStep,
    nextSubStep,
    previousSubStep,
    goToSubStep,
    resetSubSteps,
  };
}
